import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { device } from '../../styles/responsive';

const AboutSection = styled.section`
  min-height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 0 120px;
  position: relative;
  background: #000;
  
  @media ${device.lg} {
    padding: 0 80px;
  }
  
  @media ${device.md} {
    padding: 80px 40px;
  }
  
  @media ${device.sm} {
    padding: 60px 20px;
  }
`;

const ContentWrapper = styled(motion.div)`
  max-width: 1200px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const Name = styled(motion.h1)`
  font-size: 40px;
  color: white;
  margin-bottom: 55px;
  font-weight: 500;
  opacity: 0.9;
  
  @media ${device.md} {
    font-size: 32px;
    margin-bottom: 40px;
  }
  
  @media ${device.sm} {
    font-size: 28px;
    margin-bottom: 30px;
  }
`;

const TaglineContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  margin-bottom: 55px;
  width: 100%;
  
  @media ${device.md} {
    gap: 8px;
    margin-bottom: 40px;
  }
  
  @media ${device.sm} {
    margin-bottom: 30px;
  }
`;

const TagLine = styled(motion.h2)`
  font-size: 85px;
  color: white;
  font-weight: 700;
  line-height: 1.2;

  span {
    color: #07CDCD;
  }
  
  @media ${device.lg} {
    font-size: 70px;
  }
  
  @media ${device.md} {
    font-size: 54px;
  }
  
  @media ${device.sm} {
    font-size: 42px;
  }
  
  @media ${device.xs} {
    font-size: 32px;
  }
`;

const Description = styled(motion.p)`
  font-size: 30px;
  color: white;
  max-width: 8000px;
  line-height: 1.4;
  margin-bottom: 75px;
  opacity: 0.9;
  text-align: center;

  span {
    color: #07CDCD;
  }
  
  @media ${device.lg} {
    font-size: 26px;
    margin-bottom: 60px;
  }
  
  @media ${device.md} {
    font-size: 22px;
    margin-bottom: 50px;
    line-height: 1.3;
  }
  
  @media ${device.sm} {
    font-size: 18px;
    margin-bottom: 40px;
  }
`;

const SkillsList = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px 100px;
  max-width: 1000px;
  width: 100%;
  justify-items: center;
  
  @media ${device.md} {
    gap: 20px 40px;
  }
  
  @media ${device.sm} {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const SkillItem = styled(motion.div)`
  display: flex;
  align-items: center;
  padding: 12px 24px;
  color: white;
  font-size: 18px;
  opacity: 0.9;
  background: rgba(7, 205, 205, 0.05);
  border: 1px solid rgba(7, 205, 205, 0.2);
  border-radius: 30px;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  width: 100%;
  justify-content: center;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(
      circle at center,
      rgba(7, 205, 205, 0.1) 0%,
      transparent 70%
    );
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    transform: translateY(-3px);
    border-color: rgba(7, 205, 205, 0.4);
    box-shadow: 0 5px 15px rgba(7, 205, 205, 0.1);

    &::before {
      opacity: 1;
    }
  }
  
  @media ${device.md} {
    font-size: 16px;
    padding: 10px 20px;
  }
  
  @media ${device.sm} {
    font-size: 15px;
    padding: 10px 16px;
  }
`;

const About = () => {
  const [ref, inView] = useInView({
    threshold: 0.3,
    triggerOnce: false
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };

  const skills = [
    'Full-Stack Development',
    'AI-Powered Applications',
    'Machine Learning Development',
    'Collaborative AI & ML Engineering',
    'Learning new technologies',
    'Scalable AI Architectures'
  ];

  return (
    <AboutSection ref={ref} id="about">
      <ContentWrapper
        variants={containerVariants}
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
      >
        <Name variants={itemVariants}>
          Mohammad Qataadah Zakir
        </Name>

        <TaglineContainer>
          <TagLine variants={itemVariants}>
            Code with <span>Purpose</span>
          </TagLine>
          <TagLine variants={itemVariants}>
            Create with <span>Passion</span>
          </TagLine>
        </TaglineContainer>

        <Description variants={itemVariants}>
          I'm a passionate <span>software engineer focused on AI & ML</span>, with a knack for applying
          engineering principles to develop <span>intelligent</span> solutions and
          transform ideas into robust, scalable <span>AI-driven</span> systems.
        </Description>

        <SkillsList variants={containerVariants}>
          {skills.map((skill, index) => (
            <SkillItem
              key={index}
              variants={itemVariants}
            >
              {skill}
            </SkillItem>
          ))}
        </SkillsList>
      </ContentWrapper>
    </AboutSection>
  );
};

export default About;